import type { HeadingTypes, SpacingTypes } from '@etg/wings';
import { Card, Inline, Stack, Text, textVariants, vars } from '@etg/wings';
import { Accordion } from '@eti/components';
import { css } from '@eti/styles';
import { useTranslation } from '@eti/providers';
import TitledCard from '../../../../common/components/TitledCard';
import type { FaqSectionType } from '../../models';
import { bulletStyles } from '../../../../utils/styleUtils';
import { routes } from '../../../../constants/routesAndApisConstants';

const accordionWrapperStyles = css`
  border: 1px solid ${vars.colors.divider};
`;

interface FaqSectionProps {
  className?: string;
  headingAs?: `h${HeadingTypes['level']}` | 'h5';
  headingLevel?: HeadingTypes['level'];
  padding?: SpacingTypes;
  shouldRedirectToContactUs?: boolean;
  shouldShowRedirect?: boolean;
  section: FaqSectionType;
}

const FaqSection = ({
  className,
  headingAs,
  headingLevel,
  shouldRedirectToContactUs = false,
  padding = 16,
  section,
  shouldShowRedirect = true,
}: FaqSectionProps) => {
  const { t } = useTranslation();
  const link = shouldRedirectToContactUs
    ? { href: routes.ORDER_DETAILS_CONTACT_US, text: t('OrderDetails.FAQ.Link.Text') }
    : {
        href: t('ContactUs.FAQ.ButtonLink'),
        text: t('ContactUs.FAQ.ButtonLabel'),
        target: '_blank',
      };

  return (
    <TitledCard
      className={className}
      data-testid={`faq-${section.slug}`}
      headingAs={headingAs}
      headingLevel={headingLevel}
      padding={padding}
      title={section.title || ''}
    >
      <Stack>
        <Stack spacing={8}>
          {section.questionAndAnswers.map((questionAndAnswer) => (
            <Card className={accordionWrapperStyles} key={questionAndAnswer.slug}>
              <Accordion id={`faq-${section.slug}-${questionAndAnswer.slug}`} variant="full-width">
                <Accordion.Header alignY="top">
                  <Text
                    data-testid={`faq-${section.slug}-${questionAndAnswer.slug}-question`}
                    variant={textVariants._16_BOLD}
                  >
                    {questionAndAnswer.question}
                  </Text>
                </Accordion.Header>
                <Accordion.Body>
                  <Text
                    asHtml
                    className={bulletStyles()}
                    data-testid={`faq-${section.slug}-${questionAndAnswer.slug}-answer`}
                  >
                    {questionAndAnswer.answer}
                  </Text>
                </Accordion.Body>
              </Accordion>
            </Card>
          ))}
        </Stack>
        {shouldShowRedirect && (
          <Inline align="center">
            <a
              aria-label={
                shouldRedirectToContactUs
                  ? link.text
                  : `${link.text}, ${t('ScreenReader.Link.OpensInNewTab.AriaLabel.Text')}`
              }
              data-testid="faq-link"
              href={link.href}
              rel="noreferrer"
              target={link.target}
            >
              <Text>{link.text}</Text>
            </a>
          </Inline>
        )}
      </Stack>
    </TitledCard>
  );
};

export default FaqSection;
